import { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Mail, Server, SendHorizonal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { API } from '@/App';
import AdminLayout from '@/components/AdminLayout';

const AdminMailSettings = () => {
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [settings, setSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_use_tls: true,
    sender_name: 'Ener Otomasyon',
    sender_email: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/mail-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching mail settings:', error);
      toast.error('Mail ayarları yüklenirken hata oluştu');
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTestMail = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      toast.error('Lütfen geçerli bir e-posta adresi girin');
      return;
    }
    setTestLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/mail-settings/test`, { recipient: testEmail }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || 'Test maili başarıyla gönderildi!');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Test maili gönderilemedi. SMTP ayarlarınızı kontrol edin.';
      toast.error(detail);
    } finally {
      setTestLoading(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!settings.smtp_host || !settings.smtp_username || !settings.sender_email) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/mail-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mail ayarları başarıyla güncellendi!');
    } catch (error) {
      console.error('Error updating mail settings:', error);
      toast.error('Mail ayarları güncellenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Mail Ayarları</h1>
            <p className="text-gray-600 mt-1">SMTP mail sunucusu ayarlarını yapılandırın</p>
          </div>
          <Button onClick={handleSave} disabled={loading} size="lg">
            <Save className="mr-2 h-4 w-4" />
            {loading ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </div>

        <div className="space-y-6">
          {/* SMTP Server Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5" />
                SMTP Sunucu Ayarları
              </CardTitle>
              <CardDescription>
                Mail gönderimi için SMTP sunucu bilgilerini girin
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="smtp_host">SMTP Host *</Label>
                  <Input
                    id="smtp_host"
                    value={settings.smtp_host}
                    onChange={(e) => handleChange('smtp_host', e.target.value)}
                    placeholder="smtp.gmail.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtp_port">SMTP Port *</Label>
                  <Input
                    id="smtp_port"
                    type="number"
                    value={settings.smtp_port}
                    onChange={(e) => handleChange('smtp_port', parseInt(e.target.value))}
                    placeholder="587"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="smtp_username">SMTP Kullanıcı Adı *</Label>
                <Input
                  id="smtp_username"
                  value={settings.smtp_username}
                  onChange={(e) => handleChange('smtp_username', e.target.value)}
                  placeholder="your-email@gmail.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="smtp_password">SMTP Şifre</Label>
                <Input
                  id="smtp_password"
                  type="password"
                  value={settings.smtp_password}
                  onChange={(e) => handleChange('smtp_password', e.target.value)}
                  placeholder="••••••••"
                />
                <p className="text-xs text-gray-500">
                  Gmail kullanıyorsanız, 2FA aktifse "Uygulama Şifresi" oluşturmanız gerekir
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="smtp_use_tls"
                  checked={settings.smtp_use_tls}
                  onCheckedChange={(checked) => handleChange('smtp_use_tls', checked)}
                />
                <Label htmlFor="smtp_use_tls">TLS Kullan (Önerilen)</Label>
              </div>
            </CardContent>
          </Card>

          {/* Sender Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Gönderen Bilgileri
              </CardTitle>
              <CardDescription>
                Giden maillerde görünecek gönderen bilgileri
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="sender_name">Gönderen Adı</Label>
                <Input
                  id="sender_name"
                  value={settings.sender_name}
                  onChange={(e) => handleChange('sender_name', e.target.value)}
                  placeholder="Ener Otomasyon"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sender_email">Gönderen E-posta *</Label>
                <Input
                  id="sender_email"
                  type="email"
                  value={settings.sender_email}
                  onChange={(e) => handleChange('sender_email', e.target.value)}
                  placeholder="info@enerotomasyon.com.tr"
                />
              </div>
            </CardContent>
          </Card>

          {/* Test Mail */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <SendHorizonal className="h-5 w-5" />
                Test Maili Gönder
              </CardTitle>
              <CardDescription>
                SMTP ayarlarınızın doğru çalıştığını test edin
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@ornek.com"
                  className="flex-1"
                />
                <Button onClick={handleTestMail} disabled={testLoading} variant="outline">
                  <SendHorizonal className="mr-2 h-4 w-4" />
                  {testLoading ? 'Gönderiliyor...' : 'Gönder'}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Yukarıdaki SMTP ayarlarını kaydettikten sonra, buraya bir e-posta adresi yazıp test maili gönderin
              </p>
            </CardContent>
          </Card>

          {/* Help Card */}
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-blue-900">Yaygın SMTP Ayarları</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-blue-800 space-y-2">
              <div>
                <strong>Gmail:</strong>
                <ul className="list-disc ml-5 mt-1">
                  <li>Host: smtp.gmail.com</li>
                  <li>Port: 587 (TLS) veya 465 (SSL)</li>
                  <li>2FA aktifse: Uygulama Şifresi kullanın</li>
                </ul>
              </div>
              <div>
                <strong>Outlook/Office365:</strong>
                <ul className="list-disc ml-5 mt-1">
                  <li>Host: smtp.office365.com</li>
                  <li>Port: 587</li>
                </ul>
              </div>
              <div>
                <strong>Yahoo:</strong>
                <ul className="list-disc ml-5 mt-1">
                  <li>Host: smtp.mail.yahoo.com</li>
                  <li>Port: 587</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminMailSettings;
